import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useChannels, POPUP_SIZES } from "../context/ChannelsContext";
import TelegramChat from "../components/TelegramChat";
import {
  MessageSquare, Send, ExternalLink, CheckCircle2, AlertCircle, Loader2, Smartphone, ShieldCheck,
} from "lucide-react";

export default function MieiCanali() {
  const { openChannel, openState, pinned, setPopupSize: setDockPopupSize } = useChannels();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Telegram login state
  const [tgPhone, setTgPhone] = useState("");
  const [tgCode, setTgCode] = useState("");
  const [tgPwd, setTgPwd] = useState("");
  const [tgStage, setTgStage] = useState("phone"); // phone | code | done
  const [tgWorking, setTgWorking] = useState(false);
  const [tgNeed2fa, setTgNeed2fa] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/integrations/me");
      setData(r.data);
      if (r.data?.whatsapp_web?.popup_size) setDockPopupSize(r.data.whatsapp_web.popup_size);
      if (r.data?.telegram?.phone) setTgPhone(r.data.telegram.phone);
      if (r.data?.telegram?.connected) setTgStage("done");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateWaSettings = async (patch) => {
    setSaving(true);
    try {
      const next = { ...(data?.whatsapp_web || { enabled: true, popup_size: "medium" }), ...patch };
      const r = await api.put("/integrations/me/whatsapp-web", next);
      setData(r.data);
      if (patch.popup_size) setDockPopupSize(patch.popup_size);
      toast.success("Preferenze salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChannel = (key) => {
    const ok = openChannel(key);
    if (ok === false) toast.error("Popup bloccato dal browser. Abilita i popup per questo sito.");
  };

  const tgSendCode = async (e) => {
    e?.preventDefault();
    if (!tgPhone.trim()) { toast.error("Inserisci il numero di telefono"); return; }
    setTgWorking(true);
    try {
      await api.post("/integrations/telegram/send-code", { phone: tgPhone.trim() });
      toast.success("Codice inviato — controlla l'app Telegram sul tuo telefono");
      setTgNeed2fa(false);
      setTgStage("code");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTgWorking(false);
    }
  };

  const tgVerify = async (e) => {
    e?.preventDefault();
    setTgWorking(true);
    try {
      const r = await api.post("/integrations/telegram/verify-code", {
        phone: tgPhone.trim(), code: tgCode.trim(), password: tgPwd,
      });
      if (r.data?.status === "2fa_required") {
        setTgNeed2fa(true);
        toast.info("Password 2FA richiesta: inseriscila e premi Conferma di nuovo.", { duration: 6000 });
      } else {
        toast.success("Account Telegram collegato");
        setTgNeed2fa(false);
        setTgStage("done");
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTgWorking(false);
    }
  };

  const tgDisconnect = async () => {
    if (!window.confirm("Scollegare il tuo account Telegram?")) return;
    try {
      await api.delete("/integrations/telegram");
      toast.success("Telegram scollegato");
      setTgStage("phone"); setTgCode(""); setTgPwd(""); setTgNeed2fa(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const waSettings = data?.whatsapp_web || { enabled: true, popup_size: "medium" };
  const tgState = data?.telegram || {};

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="page-miei-canali">
      <div className="flex items-start gap-3 mb-2">
        <div className="p-2.5 rounded-xl" style={{ background: "rgba(27,94,32,0.10)" }}>
          <MessageSquare className="w-6 h-6" style={{ color: "var(--brand-green)" }} />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
            I miei canali
          </h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Collega WhatsApp Web e Telegram al tuo account utente. Ogni utente vede solo i propri canali.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card-shell p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {/* ---------- WHATSAPP ---------- */}
          <div className="card-shell p-5" data-testid="card-whatsapp-web">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg" style={{ background: "#DCFCE7" }}>
                <Smartphone className="w-5 h-5" style={{ color: "#15803D" }} />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>WhatsApp Web</h2>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Apri WhatsApp Web in una finestra popup affiancata al CRM
                </p>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg text-xs" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}>
                <ShieldCheck className="w-4 h-4 inline mr-1" />
                Nessuna credenziale viene salvata sul server. La sessione resta sul tuo browser, esattamente come web.whatsapp.com normale.
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  Dimensione finestra popup
                </label>
                <select
                  value={waSettings.popup_size}
                  onChange={(e) => updateWaSettings({ popup_size: e.target.value })}
                  disabled={saving}
                  className="form-input mt-1"
                  data-testid="wa-popup-size"
                >
                  {Object.entries(POPUP_SIZES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => handleOpenChannel("whatsapp")}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white hover:brightness-110"
                style={{ background: "#15803D" }}
                data-testid="btn-open-whatsapp-web"
              >
                <ExternalLink className="w-4 h-4" /> Apri WhatsApp Web
              </button>

              <div className="text-[11px] pt-1" style={{ color: "var(--text-muted)" }}>
                Nota: per motivi di sicurezza di WhatsApp, la finestra si apre come finestra esterna e non può essere "ripristinata" dal CRM una volta minimizzata (limite imposto da WhatsApp). Telegram invece resta agganciato e si riapre dal dock.
              </div>
            </div>
          </div>

          {/* ---------- TELEGRAM ---------- */}
          <div className="card-shell p-5" data-testid="card-telegram">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg" style={{ background: "#DBEAFE" }}>
                <Send className="w-5 h-5" style={{ color: "#1E40AF" }} />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Telegram personale</h2>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Collega il tuo account Telegram per leggere e inviare messaggi dal CRM
                </p>
              </div>
              {tgState.connected && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "#DCFCE7", color: "#166534" }}>
                  <CheckCircle2 className="w-3 h-3" /> Collegato
                </span>
              )}
            </div>

            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg text-xs" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
                <ShieldCheck className="w-4 h-4 inline mr-1" />
                Connessione reale e sicura via Telethon (MTProto). Esegui il login una volta con numero + codice (e password 2FA se attiva): leggi e invii i messaggi direttamente qui dentro al CRM. La sessione è salvata cifrata sul server.
              </div>

              {tgStage === "phone" && (
                <form onSubmit={tgSendCode} className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    Numero di telefono Telegram
                  </label>
                  <input
                    type="tel" required value={tgPhone} onChange={(e) => setTgPhone(e.target.value)}
                    placeholder="+39 333 1234567"
                    className="form-input"
                    data-testid="tg-phone-input"
                  />
                  <button type="submit" disabled={tgWorking} className="btn-primary w-full" data-testid="btn-tg-send-code">
                    {tgWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Invia codice
                  </button>
                </form>
              )}

              {tgStage === "code" && (
                <form onSubmit={tgVerify} className="space-y-2">
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                    Inserisci il codice ricevuto su Telegram al numero <b>{tgPhone}</b>.
                  </p>
                  <input
                    type="text" required value={tgCode} onChange={(e) => setTgCode(e.target.value)}
                    placeholder="Codice (5 cifre)" className="form-input" data-testid="tg-code-input"
                  />
                  {tgNeed2fa && (
                    <p className="text-[11px]" style={{ color: "#B45309" }}>
                      <AlertCircle className="w-3 h-3 inline mr-1" />
                      Verifica in due passaggi attiva: inserisci la password 2FA e premi di nuovo Conferma.
                    </p>
                  )}
                  <input
                    type="password" value={tgPwd} onChange={(e) => setTgPwd(e.target.value)}
                    placeholder="Password 2FA (se attiva)" className="form-input"
                    style={tgNeed2fa ? { borderColor: "#F59E0B" } : undefined}
                    data-testid="tg-pwd-input"
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setTgStage("phone")} className="btn-secondary flex-1" disabled={tgWorking}>Indietro</button>
                    <button type="submit" disabled={tgWorking} className="btn-primary flex-1" data-testid="btn-tg-verify">
                      {tgWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Conferma
                    </button>
                  </div>
                </form>
              )}

              {tgStage === "done" && (
                <div className="space-y-2">
                  <div className="p-3 rounded-lg" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                    <CheckCircle2 className="w-4 h-4 inline mr-1" style={{ color: "#15803D" }} />
                    Account collegato come <b>{tgState.phone || tgPhone}</b>
                  </div>
                  <button onClick={tgDisconnect} className="btn-secondary w-full" data-testid="btn-tg-disconnect">
                    Scollega Telegram
                  </button>
                </div>
              )}

              <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                <button
                  onClick={() => handleOpenChannel("telegram")}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:bg-black/5"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="btn-open-telegram-web"
                >
                  <ExternalLink className="w-4 h-4" /> {(openState.telegram || pinned.telegram) ? "Mostra Telegram Web" : "Apri Telegram Web"}
                </button>
                <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                  Apre la Telegram Web ufficiale agganciata al CRM. Riapribile da ogni sezione tramite il dock in basso a destra.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && tgState.connected && (
        <div className="space-y-2" data-testid="telegram-chat-section">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5" style={{ color: "#1E40AF" }} />
            <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Chat Telegram</h2>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {tgState.username ? `@${tgState.username}` : tgState.phone}
            </span>
          </div>
          <TelegramChat />
        </div>
      )}
    </div>
  );
}
